import api from "./api";

/**
 * Validates and normalizes a UUID string
 * @param {string|any} uuid - UUID to validate
 * @returns {string|null} - Validated and trimmed UUID, or null if invalid
 */
const validateAndNormalizeUuid = (uuid) => {
  if (uuid === null || uuid === undefined) {
    return null;
  }

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const trimmedUuid =
    typeof uuid === "string" ? uuid.trim() : String(uuid).trim();

  if (
    !trimmedUuid ||
    trimmedUuid === "" ||
    trimmedUuid === "optional-uuid" ||
    trimmedUuid === "null" ||
    trimmedUuid === "undefined"
  ) {
    return null;
  }

  return uuidRegex.test(trimmedUuid) ? trimmedUuid : null;
};

/**
 * Get department list with filters and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 20)
 * @param {Array<string>} params.order_by - Fields to order by
 * @param {Array<string>} params.order_direction - Order direction (ASC/DESC)
 * @param {Object} params.search - Search object with keyword
 * @param {Object} params.filters - Filter object
 * @returns {Promise<Object>} API response with body and meta
 */
export const getDepartmentList = async (params = {}) => {
  const {
    page = 1,
    page_size = 20,
    search,
    order_by = "createdAt",
    order_direction = "DESC",
  } = params;

  let response = null;

  const body = {
    page,
    page_size,
    order_by: Array.isArray(order_by) ? order_by[0] : order_by,
    order_direction: Array.isArray(order_direction)
      ? order_direction[0]
      : order_direction,
  };

  if (search && search.keyword) {
    body.search = {
      keyword: search.keyword,
    };
  }

  try {
    response = await api.post("/v1/department/list", body);
    const responseData = response.data;

    if (!responseData) {
      throw new Error("Invalid response from server");
    }

    const statusCode =
      responseData.status_code ||
      responseData.statusCode ||
      response?.status ||
      200;
    const msgCode = responseData.msg_code || responseData.msgCode || [];
    const responseBody = responseData.body || [];
    const meta = responseData.meta || {};

    const hasError =
      responseData.error ||
      (typeof responseData.msgCode === "string" &&
        responseData.msgCode.includes("INVALID")) ||
      (typeof responseData.msgCode === "string" &&
        responseData.msgCode.includes("ERROR")) ||
      responseData.message;

    if (hasError) {
      const errorMessage =
        responseData.message || responseData.error || "API returned an error";
      console.warn(
        `API error detected: ${errorMessage} (msgCode: ${responseData.msgCode})`
      );
      throw new Error(errorMessage);
    }

    if (statusCode >= 200 && statusCode < 300) {
      return {
        status_code: statusCode,
        msg_code: msgCode,
        request_id: responseData.request_id || responseData.requestId,
        body: Array.isArray(responseBody) ? responseBody : [],
        meta: meta,
      };
    }

    const errorMessage =
      msgCode.length > 0
        ? msgCode[0].description ||
          msgCode[0].code ||
          "An error occurred while listing departments."
        : "An error occurred while listing departments.";

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };

    throw error;
  } catch (error) {
    console.warn("API call failed, falling back to mock data:", error.message);

    try {
      response = await fetch("/mock-department-list.json");
      const data = await response.json();

      let filteredData = [...data.body];

      if (search && search.keyword) {
        const keyword = search.keyword.toLowerCase();
        filteredData = filteredData.filter((department) => {
          const deptName = department.departmentName || "";
          const ownerName = department.ownerName || "";
          return (
            deptName.toLowerCase().includes(keyword) ||
            ownerName.toLowerCase().includes(keyword)
          );
        });
      }

      filteredData.sort((a, b) => {
        const field = Array.isArray(order_by) ? order_by[0] : order_by;
        const direction = Array.isArray(order_direction)
          ? order_direction[0].toUpperCase()
          : order_direction.toUpperCase();
        let valA = a[field];
        let valB = b[field];

        if (field === "createdAt" || field === "updatedAt") {
          valA = new Date(valA);
          valB = new Date(valB);
        }

        if (valA < valB) return direction === "ASC" ? -1 : 1;
        if (valA > valB) return direction === "ASC" ? 1 : -1;
        return 0;
      });

      const totalItems = filteredData.length;
      const startIndex = (page - 1) * page_size;
      const endIndex = startIndex + page_size;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      return {
        status_code: 200,
        msg_code: data.msg_code,
        request_id: data.request_id,
        body: paginatedData,
        meta: {
          total_items: totalItems,
          total_pages: Math.ceil(totalItems / page_size),
          current_page: page,
          per_page: page_size,
          has_next: endIndex < totalItems,
          has_previous: startIndex > 0,
          next_cursor: null,
        },
      };
    } catch (mockError) {
      console.error("Error fetching mock department data:", mockError);
      return {
        status_code: 500,
        msg_code: [
          {
            code: "DEPARTMENT_LIST_ERROR",
            description:
              "An unexpected error occurred while listing departments.",
          },
        ],
        body: null,
        meta: null,
      };
    }
  }
};

/**
 * Get sub-departments list (uses /v1/department/list with department_uuid filter)
 * @param {Object} params - Query parameters
 * @param {string} params.department_uuid - Parent department UUID (required for sub-departments)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 20)
 * @param {string} params.order_by - Field to order by (default: "created_at")
 * @param {string} params.order_direction - Order direction (default: "DESC")
 * @param {Object} params.search - Search object with keyword
 * @returns {Promise<Object>} API response with body and meta
 */
export const getSubDepartmentsList = async (params = {}) => {
  const {
    department_uuid,
    page = 1,
    page_size = 20,
    order_by = "created_at",
    order_direction = "DESC",
    search,
  } = params;

  const body = {
    page,
    page_size,
    order_by,
    order_direction,
  };

  // Validate and include department_uuid if provided
  const validatedUuid = validateAndNormalizeUuid(department_uuid);
  if (validatedUuid) {
    body.department_uuid = validatedUuid;
  }

  if (search && search.keyword) {
    body.search = {
      keyword: search.keyword,
    };
  }

  try {
    const response = await api.post("/v1/department/list", body);
    const responseData = response.data;

    if (!responseData) {
      throw new Error("Invalid response from server");
    }

    const statusCode =
      responseData.status_code ||
      responseData.statusCode ||
      response?.status ||
      200;
    const msgCode = responseData.msg_code || responseData.msgCode || [];

    if (statusCode >= 200 && statusCode < 300) {
      return {
        status_code: statusCode,
        msg_code: msgCode,
        request_id:
          responseData.request_id ||
          responseData.requestId ||
          `req-${Date.now()}`,
        body: Array.isArray(responseData.body) ? responseData.body : [],
        meta: responseData.meta || null,
      };
    }

    const errorMessage =
      msgCode.length > 0
        ? msgCode[0].description ||
          msgCode[0].code ||
          "An error occurred while listing sub-departments."
        : "An error occurred while listing sub-departments.";

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };

    throw error;
  } catch (error) {
    console.error("Error fetching sub-departments:", error);
    throw error;
  }
};

/**
 * Transform mock department data to contract format (camelCase)
 * @param {Object} mockDept - Department from mock JSON
 * @returns {Object} Transformed department in contract format
 */
const transformMockDepartment = (mockDept) => {
  const deptUuid = mockDept.department_uuid || mockDept.departmentUuid;

  return {
    departmentUuid: deptUuid,
    departmentName: mockDept.department_name || mockDept.departmentName,
    departmentTag: mockDept.tags || mockDept.department_tag || [],
    description: mockDept.description || "",
    status: mockDept.status || "ACTIVE",
    groupUuid: `group-${deptUuid}`,
    ownerUuid: mockDept.owner_uuid || mockDept.ownerUuid || null,
    ownerName: mockDept.owner_name || mockDept.ownerName || "",
    ownerPhotoUrl:
      mockDept.photo_url ||
      mockDept.owner_photo_url ||
      mockDept.ownerPhotoUrl ||
      null,
    ownerRole: "Department Admin",
    userCount: mockDept.total_active_members || mockDept.userCount || 0,
    knowledgeBaseCount: mockDept.total_kbu || mockDept.knowledgeBaseCount || 0,
    knowledgeBase: mockDept.knowledge_bases || mockDept.knowledgeBase || [],
    createdAt: mockDept.created_at || mockDept.createdAt,
    updatedAt: mockDept.updated_at || mockDept.updatedAt || null,
    agentWorkflows: 0,
    overrides: 0,
    feedbackItems: 0,
  };
};

/**
 * Get department by ID/UUID
 * @param {string} departmentId - Department ID/UUID
 * @returns {Promise<Object>} API response with department data in contract format
 */
export const getDepartmentById = async (departmentId) => {
  if (!departmentId) {
    throw new Error("departmentId is required");
  }

  const trimmedUuid = String(departmentId).trim();
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  if (!uuidRegex.test(trimmedUuid)) {
    return {
      statusCode: 400,
      msgCode: [
        {
          code: "DEPARTMENT_INVALID_UUID",
          description: "The provided department_uuid is not a valid UUID.",
        },
      ],
      requestId: `mock-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  try {
    const response = await api.get(`/v1/department/${trimmedUuid}`);
    const responseData = response.data;

    if (!responseData) {
      throw new Error("Invalid response from server");
    }

    const statusCode =
      responseData.statusCode ||
      responseData.status_code ||
      response?.status ||
      200;
    const msgCode = responseData.msgCode || responseData.msg_code || [];
    const department = responseData.body;

    if (statusCode >= 200 && statusCode < 300 && department) {
      return {
        statusCode: statusCode,
        msgCode: msgCode,
        requestId:
          responseData.requestId ||
          responseData.request_id ||
          `req-${Date.now()}`,
        body: department,
        meta: responseData.meta || null,
      };
    }

    const errorMessage =
      msgCode.length > 0
        ? msgCode[0].description ||
          msgCode[0].code ||
          "An error occurred while fetching department."
        : "An error occurred while fetching department.";

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };

    throw error;
  } catch (error) {
    console.warn(
      "API call failed for getDepartmentById, falling back to mock:",
      error.message
    );

    try {
      const mockResponse = await fetch("/mock-department-list.json");
      const mockData = await mockResponse.json();

      const mockDepartment = (mockData.body || []).find(
        (dept) => (dept.department_uuid || dept.departmentUuid) === trimmedUuid
      );

      if (!mockDepartment) {
        return {
          statusCode: 404,
          msgCode: [
            {
              code: "DEPARTMENT_NOT_FOUND",
              description: `Department not found for the given UUID: ${trimmedUuid}`,
            },
          ],
          requestId: `mock-${Date.now()}`,
          body: null,
          meta: null,
        };
      }

      return {
        statusCode: 200,
        msgCode: [
          {
            code: "DEPARTMENT_DETAIL_RETRIEVED",
            description: "Department detail retrieved successfully.",
          },
        ],
        requestId:
          mockData.request_id || mockData.requestId || `mock-${Date.now()}`,
        body: transformMockDepartment(mockDepartment),
        meta: null,
      };
    } catch (mockError) {
      console.error("Error fetching mock department data:", mockError);
      return {
        statusCode: 500,
        msgCode: [
          {
            code: "DEPARTMENT_DETAIL_INTERNAL_ERROR",
            description:
              "An unexpected error occurred while retrieving department detail.",
          },
        ],
        requestId: `mock-${Date.now()}`,
        body: null,
        meta: null,
      };
    }
  }
};

const getMockCreateDepartment = async (params = {}) => {
  await new Promise((resolve) => setTimeout(resolve, 500));

  const { code, name, description, ownerUuid, parentDepartmentUuid } = params;

  if (!code || !code.trim()) {
    return {
      status_code: 400,
      statusCode: 400,
      msg_code: [
        {
          code: "DEPARTMENT_CODE_REQUIRED",
          description: "Department code is required",
        },
      ],
      msgCode: [
        {
          code: "DEPARTMENT_CODE_REQUIRED",
          description: "Department code is required",
        },
      ],
      request_id: `req-${Date.now()}`,
      requestId: `req-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  const codeRegex = /^[A-Z0-9_]+$/;
  if (!codeRegex.test(code)) {
    return {
      status_code: 400,
      statusCode: 400,
      msg_code: [
        {
          code: "INVALID_DEPARTMENT_CODE",
          description:
            "Department code must be uppercase alphanumeric with underscores only (e.g., SERVICE, SALES, HR_DEPT_001)",
        },
      ],
      msgCode: [
        {
          code: "INVALID_DEPARTMENT_CODE",
          description:
            "Department code must be uppercase alphanumeric with underscores only (e.g., SERVICE, SALES, HR_DEPT_001)",
        },
      ],
      request_id: `req-${Date.now()}`,
      requestId: `req-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  if (!name || !name.trim()) {
    return {
      status_code: 400,
      statusCode: 400,
      msg_code: [
        {
          code: "DEPARTMENT_NAME_REQUIRED",
          description: "Department name is required",
        },
      ],
      msgCode: [
        {
          code: "DEPARTMENT_NAME_REQUIRED",
          description: "Department name is required",
        },
      ],
      request_id: `req-${Date.now()}`,
      requestId: `req-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  if (name.trim().length > 255) {
    return {
      status_code: 400,
      statusCode: 400,
      msg_code: [
        {
          code: "DEPARTMENT_NAME_REQUIRED",
          description: "Department name must be at most 255 characters",
        },
      ],
      msgCode: [
        {
          code: "DEPARTMENT_NAME_REQUIRED",
          description: "Department name must be at most 255 characters",
        },
      ],
      request_id: `req-${Date.now()}`,
      requestId: `req-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  if (description && description.length > 500) {
    return {
      status_code: 400,
      statusCode: 400,
      msg_code: [
        {
          code: "DEPARTMENT_INVALID_DESCRIPTION",
          description: "Description must be at most 500 characters",
        },
      ],
      msgCode: [
        {
          code: "DEPARTMENT_INVALID_DESCRIPTION",
          description: "Description must be at most 500 characters",
        },
      ],
      request_id: `req-${Date.now()}`,
      requestId: `req-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (!ownerUuid || !uuidRegex.test(ownerUuid)) {
    return {
      status_code: 400,
      statusCode: 400,
      msg_code: [
        {
          code: "DEPARTMENT_OWNER_REQUIRED",
          description: "Department owner UUID is required",
        },
      ],
      msgCode: [
        {
          code: "DEPARTMENT_OWNER_REQUIRED",
          description: "Department owner UUID is required",
        },
      ],
      request_id: `req-${Date.now()}`,
      requestId: `req-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  if (parentDepartmentUuid && !uuidRegex.test(parentDepartmentUuid)) {
    return {
      status_code: 404,
      statusCode: 404,
      msg_code: [
        {
          code: "PARENT_DEPARTMENT_NOT_FOUND",
          description: `Parent department not found: ${parentDepartmentUuid}`,
        },
      ],
      msgCode: [
        {
          code: "PARENT_DEPARTMENT_NOT_FOUND",
          description: `Parent department not found: ${parentDepartmentUuid}`,
        },
      ],
      request_id: `req-${Date.now()}`,
      requestId: `req-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  const generateUUID = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const departmentUuid = generateUUID();
  const groupUuid = generateUUID();
  const groupTenantTreeUuid = generateUUID();

  return {
    status_code: 201,
    statusCode: 201,
    msg_code: [
      {
        code: "DEPARTMENT_CREATED",
        description: "Department created successfully.",
      },
    ],
    msgCode: [
      {
        code: "DEPARTMENT_CREATED",
        description: "Department created successfully.",
      },
    ],
    request_id: `req-${Date.now()}`,
    requestId: `req-${Date.now()}`,
    body: {
      department_uuid: departmentUuid,
      departmentUuid: departmentUuid,
      code: code.trim(),
      name: name.trim(),
      description: description || null,
      owner_uuid: ownerUuid,
      ownerUuid: ownerUuid,
      group_uuid: groupUuid,
      groupUuid: groupUuid,
      group_tenant_tree_uuid: groupTenantTreeUuid,
      groupTenantTreeUuid: groupTenantTreeUuid,
    },
    meta: null,
  };
};

export const createDepartment = async (params = {}) => {
  try {
    const validatedParentUuid = validateAndNormalizeUuid(
      params.parent_department_uuid || params.parentDepartmentUuid
    );

    let body;

    if (validatedParentUuid) {
      body = {
        departmentName: params.department_name || params.name,
        description: params.description,
        ownerUuid: params.owner_uuid || params.ownerUuid,
        parentDepartmentUuid: validatedParentUuid,
      };
    } else {
      body = {
        department_name: params.department_name || params.name,
        description: params.description,
        owner_uuid: params.owner_uuid || params.ownerUuid,
      };

      const validatedDeptUuid = validateAndNormalizeUuid(
        params.department_uuid || params.departmentUuid
      );
      if (validatedDeptUuid) {
        body.department_uuid = validatedDeptUuid;
      }
    }

    const response = await api.post("/v1/department/create", body);
    return response.data;
  } catch (error) {
    console.warn("createDepartment failed, using mock fallback", error);
    const mockResponse = await getMockCreateDepartment(params);
    return mockResponse;
  }
};

const getMockUpdateDepartment = async (params = {}) => {
  await new Promise((resolve) => setTimeout(resolve, 500));

  const { department_uuid, department_name, description, owner_uuid } = params;

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  if (!department_uuid || !uuidRegex.test(department_uuid)) {
    return {
      status_code: 400,
      statusCode: 400,
      msg_code: [
        {
          code: "DEPARTMENT_INVALID_UUID",
          description: "The provided department_uuid is not a valid UUID.",
        },
      ],
      msgCode: [
        {
          code: "DEPARTMENT_INVALID_UUID",
          description: "The provided department_uuid is not a valid UUID.",
        },
      ],
      request_id: `req-${Date.now()}`,
      requestId: `req-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  const hasDepartmentName =
    department_name !== undefined && department_name !== null;
  const hasDescription = description !== undefined && description !== null;
  const hasOwnerUuid = owner_uuid !== undefined && owner_uuid !== null;

  if (!hasDepartmentName && !hasDescription && !hasOwnerUuid) {
    return {
      status_code: 400,
      statusCode: 400,
      msg_code: [
        {
          code: "DEPARTMENT_NO_FIELDS_TO_UPDATE",
          description:
            "At least one updatable field (department_name, description, owner_uuid) must be provided.",
        },
      ],
      msgCode: [
        {
          code: "DEPARTMENT_NO_FIELDS_TO_UPDATE",
          description:
            "At least one updatable field (department_name, description, owner_uuid) must be provided.",
        },
      ],
      request_id: `req-${Date.now()}`,
      requestId: `req-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  if (hasDepartmentName) {
    const trimmedName = department_name.trim();
    if (trimmedName.length === 0) {
      return {
        status_code: 400,
        statusCode: 400,
        msg_code: [
          {
            code: "DEPARTMENT_INVALID_NAME",
            description:
              "department_name must be non-empty after trimming whitespace.",
          },
        ],
        msgCode: [
          {
            code: "DEPARTMENT_INVALID_NAME",
            description:
              "department_name must be non-empty after trimming whitespace.",
          },
        ],
        request_id: `req-${Date.now()}`,
        requestId: `req-${Date.now()}`,
        body: null,
        meta: null,
      };
    }

    if (trimmedName.length > 255) {
      return {
        status_code: 400,
        statusCode: 400,
        msg_code: [
          {
            code: "DEPARTMENT_INVALID_NAME",
            description: "department_name must be at most 255 characters.",
          },
        ],
        msgCode: [
          {
            code: "DEPARTMENT_INVALID_NAME",
            description: "department_name must be at most 255 characters.",
          },
        ],
        request_id: `req-${Date.now()}`,
        requestId: `req-${Date.now()}`,
        body: null,
        meta: null,
      };
    }
  }

  if (hasOwnerUuid && !uuidRegex.test(owner_uuid)) {
    return {
      status_code: 400,
      statusCode: 400,
      msg_code: [
        {
          code: "DEPARTMENT_INVALID_OWNER_UUID",
          description: "The provided owner_uuid is not a valid UUID.",
        },
      ],
      msgCode: [
        {
          code: "DEPARTMENT_INVALID_OWNER_UUID",
          description: "The provided owner_uuid is not a valid UUID.",
        },
      ],
      request_id: `req-${Date.now()}`,
      requestId: `req-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  const generateUUID = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const groupUuid = generateUUID();
  const groupTenantTreeUuid = generateUUID();

  return {
    status_code: 200,
    statusCode: 200,
    msg_code: [
      {
        code: "DEPARTMENT_UPDATED",
        description: "Department updated successfully.",
      },
    ],
    msgCode: [
      {
        code: "DEPARTMENT_UPDATED",
        description: "Department updated successfully.",
      },
    ],
    request_id: `req-${Date.now()}`,
    requestId: `req-${Date.now()}`,
    body: {
      department_uuid: department_uuid,
      departmentUuid: department_uuid,
      department_name: hasDepartmentName ? department_name.trim() : undefined,
      departmentName: hasDepartmentName ? department_name.trim() : undefined,
      description: hasDescription ? description : undefined,
      owner_uuid: hasOwnerUuid ? owner_uuid : undefined,
      ownerUuid: hasOwnerUuid ? owner_uuid : undefined,
      group_uuid: groupUuid,
      groupUuid: groupUuid,
      group_tenant_tree_uuid: groupTenantTreeUuid,
      groupTenantTreeUuid: groupTenantTreeUuid,
    },
    meta: null,
  };
};

export const updateDepartment = async (params = {}) => {
  try {
    const body = {
      department_uuid: params.department_uuid || params.departmentUuid,
      department_name: params.department_name || params.name,
      description: params.description,
      owner_uuid: params.owner_uuid || params.ownerUuid,
    };

    const response = await api.post("/v1/department/update", body);
    return response.data;
  } catch (error) {
    console.warn("updateDepartment failed, using mock fallback", error);
    const mockResponse = await getMockUpdateDepartment(params);
    return mockResponse;
  }
};

/**
 * Add users to department (uses /v1/department/user/add endpoint)
 *
 * @param {Object} params - Assignment parameters
 * @param {string} params.department_uuid - Department UUID (required)
 * @param {string[]} params.user_uuids - Array of user UUIDs (required)
 * @returns {Promise<Object>} API response with body containing assignment details
 * @throws {Error} If the API call fails
 */
export const addUsersToDepartment = async (params = {}) => {
  const { department_uuid, user_uuids = [] } = params;

  if (!department_uuid) {
    throw new Error("department_uuid is required");
  }

  const validatedDeptUuid = validateAndNormalizeUuid(department_uuid);
  if (!validatedDeptUuid) {
    throw new Error("Invalid department_uuid format");
  }

  if (!user_uuids || user_uuids.length === 0) {
    throw new Error("At least one user UUID is required");
  }

  // Validate all user UUIDs
  const validatedUserUuids = user_uuids
    .map((uuid) => validateAndNormalizeUuid(uuid))
    .filter((uuid) => uuid !== null);

  if (validatedUserUuids.length === 0) {
    throw new Error("At least one valid user UUID is required");
  }

  try {
    const body = {
      department_uuid: validatedDeptUuid,
      user_uuids: validatedUserUuids,
    };

    const response = await api.post("/v1/department/user/add", body);
    const responseData = response.data;

    if (!responseData) {
      throw new Error("Invalid response from server");
    }

    const statusCode =
      responseData.status_code ||
      responseData.statusCode ||
      response?.status ||
      200;
    const msgCode = responseData.msg_code || responseData.msgCode || [];
    const responseBody = responseData.body || null;

    if (statusCode >= 200 && statusCode < 300) {
      return {
        status_code: statusCode,
        statusCode: statusCode,
        msg_code: msgCode,
        msgCode: msgCode,
        request_id:
          responseData.request_id ||
          responseData.requestId ||
          `req-${Date.now()}`,
        requestId:
          responseData.request_id ||
          responseData.requestId ||
          `req-${Date.now()}`,
        body: responseBody,
        meta: responseData.meta || null,
      };
    }

    const errorMessage =
      msgCode.length > 0
        ? msgCode[0].description ||
          msgCode[0].code ||
          "An error occurred while adding users to department."
        : "An error occurred while adding users to department.";

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };
    throw error;
  } catch (error) {
    if (error.response) {
      const responseData = error.response.data;
      const statusCode =
        error.response.status ||
        responseData?.status_code ||
        responseData?.statusCode;
      const msgCode = responseData?.msg_code || responseData?.msgCode || [];

      const errorMessage =
        msgCode.length > 0
          ? msgCode[0].description ||
            msgCode[0].code ||
            "An error occurred while adding users to department."
          : error.message ||
            "An error occurred while adding users to department.";

      const apiError = new Error(errorMessage);
      apiError.response = {
        data: responseData,
        status: statusCode,
      };
      throw apiError;
    }

    if (error.request) {
      throw new Error(
        "Unable to connect to the server. Please check your internet connection and try again."
      );
    }

    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      throw new Error(
        "Request timeout. The server took too long to respond. Please try again."
      );
    }

    throw error;
  }
};

/**
 * Get users in department (uses /v1/department/user/list endpoint)
 *
 * @param {Object} params - Query parameters
 * @param {string} params.department_uuid - Department UUID (required)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 20)
 * @param {string|Array<string>} params.order_by - Field(s) to order by
 * @param {string|Array<string>} params.order_direction - Order direction(s) (ASC/DESC)
 * @param {Object} params.search - Search object with keyword
 * @param {Object} params.filters - Filter object
 * @returns {Promise<Object>} API response with body containing users list and meta
 * @throws {Error} If the API call fails
 */
export const getDepartmentUsers = async (params = {}) => {
  const {
    department_uuid,
    page = 1,
    page_size = 20,
    order_by = "created_at",
    order_direction = "DESC",
    search,
    filters,
  } = params;

  if (!department_uuid) {
    throw new Error("department_uuid is required");
  }

  const validatedDeptUuid = validateAndNormalizeUuid(department_uuid);
  if (!validatedDeptUuid) {
    throw new Error("Invalid department_uuid format");
  }

  const body = {
    department_uuid: validatedDeptUuid,
    page,
    page_size,
    order_by: Array.isArray(order_by) ? order_by : [order_by],
    order_direction: Array.isArray(order_direction)
      ? order_direction
      : [order_direction],
  };

  if (search && search.keyword) {
    body.search = {
      keyword: search.keyword,
    };
  }

  if (filters) {
    body.filters = filters;
  }

  try {
    const response = await api.post("/v1/department/user/list", body);
    const responseData = response.data;

    if (!responseData) {
      throw new Error("Invalid response from server");
    }

    const statusCode =
      responseData.status_code ||
      responseData.statusCode ||
      response?.status ||
      200;
    const msgCode = responseData.msg_code || responseData.msgCode || [];
    const responseBody = responseData.body || [];
    const meta = responseData.meta || {};

    if (statusCode >= 200 && statusCode < 300) {
      return {
        status_code: statusCode,
        statusCode: statusCode,
        msg_code: msgCode,
        msgCode: msgCode,
        request_id:
          responseData.request_id ||
          responseData.requestId ||
          `req-${Date.now()}`,
        requestId:
          responseData.request_id ||
          responseData.requestId ||
          `req-${Date.now()}`,
        body: Array.isArray(responseBody) ? responseBody : [],
        meta: meta,
      };
    }

    const errorMessage =
      msgCode.length > 0
        ? msgCode[0].description ||
          msgCode[0].code ||
          "An error occurred while listing department users."
        : "An error occurred while listing department users.";

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };
    throw error;
  } catch (error) {
    if (error.response) {
      const responseData = error.response.data;
      const statusCode =
        error.response.status ||
        responseData?.status_code ||
        responseData?.statusCode;
      const msgCode = responseData?.msg_code || responseData?.msgCode || [];

      const errorMessage =
        msgCode.length > 0
          ? msgCode[0].description ||
            msgCode[0].code ||
            "An error occurred while listing department users."
          : error.message ||
            "An error occurred while listing department users.";

      const apiError = new Error(errorMessage);
      apiError.response = {
        data: responseData,
        status: statusCode,
      };
      throw apiError;
    }

    if (error.request) {
      throw new Error(
        "Unable to connect to the server. Please check your internet connection and try again."
      );
    }

    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      throw new Error(
        "Request timeout. The server took too long to respond. Please try again."
      );
    }

    throw error;
  }
};
